import type { VisitPopulated } from '@api/READ_ONLY/visits_api/Api';

import { METRIC_LABEL_KEYS, METRIC_UNITS, type VitalMetric } from './types';

/**
 * The vitals a caregiver recorded on a visit, for the doctor to read.
 *
 * Values only. This used to flag each one red against the doctor's own alerting
 * bounds, which is the ALERTING half of a feature the product does not have —
 * a doctor set a bound and nothing ever acted on it — and which has been taken
 * off the doctor's screens (DOCTOR_THRESHOLD_ALERTS_SHOWN). Colouring a number
 * against limits the doctor can no longer see or change would be worse than not
 * colouring it: the criterion would be invisible.
 *
 * A metric with no reading is reported empty rather than hidden: the visit may
 * simply not have been carried out yet, and dropping the row would read as
 * "this is not measured here".
 */

/** The column on `visits` each metric is stored in. Mirrors METRIC_COLUMNS server-side. */
const METRIC_FIELDS: Record<VitalMetric, keyof VisitPopulated> = {
  BLOOD_PRESSURE_SYSTOLIC: 'bloodPressureSystolic',
  BLOOD_PRESSURE_DIASTOLIC: 'bloodPressureDiastolic',
  HEART_RATE: 'heartRate',
  BLOOD_GLUCOSE: 'bloodGlucose',
  TEMPERATURE: 'temperature',
};

/**
 * Vitals with no VitalMetric of their own. Listed separately because that enum is
 * the set of things the backend can key a measurement on, and it is a contract —
 * not the set of things a visit records.
 */
const EXTRA: { field: keyof VisitPopulated; labelKey: string; unit: string }[] = [
  { field: 'oxygenSaturation', labelKey: 'visits-vital-oxygen-saturation', unit: '%' },
  { field: 'painScale', labelKey: 'visits-vital-pain-scale', unit: '' },
  { field: 'fvc', labelKey: 'visits-vital-fvc', unit: 'L' },
  { field: 'fev1', labelKey: 'visits-vital-fev1', unit: 'L' },
  { field: 'fev1FvcRatio', labelKey: 'visits-vital-fev1-fvc-ratio', unit: '%' },
];

export type VitalReading = {
  key: string;
  labelKey: string;
  unit: string;
  value: number | null;
};

const numberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const readVisitVitals = (visit: VisitPopulated | undefined): VitalReading[] => {
  if (!visit) return [];

  const metrics = (Object.keys(METRIC_FIELDS) as VitalMetric[]).map((metric) => ({
    key: metric,
    labelKey: METRIC_LABEL_KEYS[metric],
    unit: METRIC_UNITS[metric],
    value: numberOrNull(visit[METRIC_FIELDS[metric]]),
  }));

  const extra = EXTRA.map(({ field, labelKey, unit }) => ({
    key: String(field),
    labelKey,
    unit,
    value: numberOrNull(visit[field]),
  }));

  return [...metrics, ...extra];
};

/** True when the visit carries no measurement at all — the caregiver recorded none. */
export const hasNoVitals = (readings: VitalReading[]): boolean => readings.every((reading) => reading.value === null);
